// Supabase経由のデータアクセス
// localStorageベースの storage.ts と同じインターフェイスを提供
import { supabase } from './supabase';
import { AnyTask, Lv1Task, Lv2Task, Lv3Task, ReactionLog } from '../types';

// ===== 家族グループ =====
export interface Family {
  id: string;
  name: string;
  inviteToken: string;
}

export interface FamilyMember {
  userId: string;
  role: 'owner' | 'member';
  displayName?: string;
}

/** 現在のユーザーが所属する家族（先頭1件）を取得 */
export async function getMyFamily(): Promise<Family | null> {
  if (!supabase) return null;
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) return null;
  const { data, error } = await supabase
    .from('ktv_family_members')
    .select('family_id, ktv_families(id, name, invite_token)')
    .eq('user_id', user.user.id)
    .limit(1)
    .maybeSingle();
  if (error) {
    console.error('[db.getMyFamily]', error);
    return null;
  }
  if (!data) return null;
  const f = (data as any).ktv_families;
  if (!f) return null;
  return { id: f.id, name: f.name, inviteToken: f.invite_token };
}

/** 新規家族を作成し、現在ユーザーをownerとして登録 */
export async function createFamily(name: string): Promise<Family | null> {
  if (!supabase) return null;
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) return null;
  const { data: family, error: e1 } = await supabase
    .from('ktv_families')
    .insert({ name })
    .select('id, name, invite_token')
    .single();
  if (e1 || !family) {
    console.error('[db.createFamily insert family]', e1);
    return null;
  }
  const { error: e2 } = await supabase.from('ktv_family_members').insert({
    family_id: family.id,
    user_id: user.user.id,
    role: 'owner',
  });
  if (e2) {
    console.error('[db.createFamily insert member]', e2);
    return null;
  }
  return { id: family.id, name: family.name, inviteToken: family.invite_token };
}

/** 招待トークン経由で既存家族に参加 */
export async function joinFamily(inviteToken: string): Promise<Family | null> {
  if (!supabase) return null;
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) return null;
  const { data: family, error: e1 } = await supabase
    .from('ktv_families')
    .select('id, name, invite_token')
    .eq('invite_token', inviteToken)
    .maybeSingle();
  if (e1 || !family) {
    console.error('[db.joinFamily lookup]', e1);
    return null;
  }
  // すでにメンバーかチェック
  const { data: existing } = await supabase
    .from('ktv_family_members')
    .select('id')
    .eq('family_id', family.id)
    .eq('user_id', user.user.id)
    .maybeSingle();
  if (!existing) {
    const { error: e2 } = await supabase.from('ktv_family_members').insert({
      family_id: family.id,
      user_id: user.user.id,
      role: 'member',
    });
    if (e2) {
      console.error('[db.joinFamily insert]', e2);
      return null;
    }
  }
  return { id: family.id, name: family.name, inviteToken: family.invite_token };
}

/** 家族メンバー一覧 */
export async function listFamilyMembers(familyId: string): Promise<FamilyMember[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('ktv_family_members')
    .select('user_id, role, display_name')
    .eq('family_id', familyId);
  if (error) {
    console.error('[db.listFamilyMembers]', error);
    return [];
  }
  return (data || []).map((r) => ({
    userId: r.user_id,
    role: r.role as 'owner' | 'member',
    displayName: r.display_name || undefined,
  }));
}

// ===== タスク =====
function dbRowToTask(row: any): AnyTask {
  const base = {
    id: row.id,
    daysOfWeek: row.days_of_week || [],
    enabled: row.enabled,
    createdAt: new Date(row.created_at).getTime(),
  };
  if (row.level === 'lv1') {
    return { ...base, level: 'lv1', time: row.time_hhmm, message: row.title } as Lv1Task;
  }
  if (row.level === 'lv2') {
    return {
      ...base,
      level: 'lv2',
      eventTime: row.time_hhmm,
      title: row.title,
      customMessages: row.custom_messages || undefined,
    } as Lv2Task;
  }
  return { ...base, level: 'lv3', eventTime: row.time_hhmm, title: row.title } as Lv3Task;
}

function taskToDbRow(t: AnyTask, familyId: string, userId: string): any {
  const common = {
    family_id: familyId,
    level: t.level,
    days_of_week: t.daysOfWeek,
    enabled: t.enabled,
    created_by: userId,
  };
  if (t.level === 'lv1') {
    return { ...common, time_hhmm: t.time, title: t.message };
  }
  if (t.level === 'lv2') {
    return {
      ...common,
      time_hhmm: t.eventTime,
      title: t.title,
      custom_messages: t.customMessages || null,
    };
  }
  return { ...common, time_hhmm: t.eventTime, title: t.title };
}

export async function loadTasksByFamily(familyId: string): Promise<AnyTask[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('ktv_tasks')
    .select('*')
    .eq('family_id', familyId)
    .order('time_hhmm');
  if (error) {
    console.error('[db.loadTasksByFamily]', error);
    return [];
  }
  return (data || []).map(dbRowToTask);
}

export async function addTaskDb(
  task: Omit<AnyTask, 'id' | 'createdAt'>,
  familyId: string,
  userId: string
): Promise<AnyTask | null> {
  if (!supabase) return null;
  const row = taskToDbRow({ ...task, id: '', createdAt: 0 } as AnyTask, familyId, userId);
  delete row.id;
  delete row.created_at;
  const { data, error } = await supabase.from('ktv_tasks').insert(row).select('*').single();
  if (error || !data) {
    console.error('[db.addTaskDb]', error);
    return null;
  }
  return dbRowToTask(data);
}

export async function updateTaskDb(taskId: string, patch: Partial<AnyTask>): Promise<boolean> {
  if (!supabase) return false;
  const update: any = {};
  if ('enabled' in patch) update.enabled = patch.enabled;
  if ('daysOfWeek' in patch) update.days_of_week = patch.daysOfWeek;
  if ('time' in patch) update.time_hhmm = (patch as Lv1Task).time;
  if ('message' in patch) update.title = (patch as Lv1Task).message;
  if ('eventTime' in patch) update.time_hhmm = (patch as Lv2Task).eventTime;
  if ('title' in patch) update.title = (patch as Lv2Task).title;
  if ('customMessages' in patch) update.custom_messages = (patch as Lv2Task).customMessages;
  const { error } = await supabase.from('ktv_tasks').update(update).eq('id', taskId);
  if (error) {
    console.error('[db.updateTaskDb]', error);
    return false;
  }
  return true;
}

export async function deleteTaskDb(taskId: string): Promise<boolean> {
  if (!supabase) return false;
  const { error } = await supabase.from('ktv_tasks').delete().eq('id', taskId);
  if (error) {
    console.error('[db.deleteTaskDb]', error);
    return false;
  }
  return true;
}

// ===== 履歴 =====
export async function appendReactionDb(
  log: Omit<ReactionLog, 'id'>,
  familyId: string
): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.from('ktv_reactions').insert({
    family_id: familyId,
    task_id: log.taskId || null,
    task_title: log.taskTitle,
    level: log.level,
    fired_at: new Date(log.firedAt).toISOString(),
    announcement: log.announcement,
    reply: log.reply || null,
    verdict: log.verdict || null,
    ignore_count: log.ignoreCount ?? 0,
  });
  if (error) {
    console.error('[db.appendReactionDb]', error);
  }
}

export async function loadReactionsByFamily(familyId: string, limit = 200): Promise<ReactionLog[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('ktv_reactions')
    .select('*')
    .eq('family_id', familyId)
    .order('fired_at', { ascending: false })
    .limit(limit);
  if (error) {
    console.error('[db.loadReactionsByFamily]', error);
    return [];
  }
  return (data || []).map((r) => ({
    id: r.id,
    taskId: r.task_id || '',
    taskTitle: r.task_title,
    level: r.level,
    firedAt: new Date(r.fired_at).getTime(),
    announcement: r.announcement,
    reply: r.reply || undefined,
    verdict: r.verdict || undefined,
    ignoreCount: r.ignore_count || 0,
  }));
}

export async function clearReactionsByFamily(familyId: string): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.from('ktv_reactions').delete().eq('family_id', familyId);
  if (error) {
    console.error('[db.clearReactionsByFamily]', error);
  }
}

// ===== Realtime購読 =====
/** タスクの変更を購読 */
export function subscribeTaskChanges(
  familyId: string,
  callback: () => void
): () => void {
  if (!supabase) return () => {};
  const channel = supabase
    .channel(`ktv_tasks:family=${familyId}`)
    .on(
      'postgres_changes' as any,
      { event: '*', schema: 'public', table: 'ktv_tasks', filter: `family_id=eq.${familyId}` },
      () => callback()
    )
    .subscribe();
  return () => {
    supabase!.removeChannel(channel);
  };
}

/** 履歴の変更を購読 */
export function subscribeReactionChanges(
  familyId: string,
  callback: () => void
): () => void {
  if (!supabase) return () => {};
  const channel = supabase
    .channel(`ktv_reactions:family=${familyId}`)
    .on(
      'postgres_changes' as any,
      { event: '*', schema: 'public', table: 'ktv_reactions', filter: `family_id=eq.${familyId}` },
      () => callback()
    )
    .subscribe();
  return () => {
    supabase!.removeChannel(channel);
  };
}
